<template>
    <div class="range-slider">
        <div
            class="slider-tooltip"
            :style="{ left: currentPercent + '%' }"
        >
            {{ formattedValue }}
        </div>

        <div
            class="slider-track"
            ref="track"
            @mousedown="startDrag"
            @touchstart="startDrag"
        >
            <div
                class="slider-fill"
                :style="{ width: currentPercent + '%' }"
            ></div>

            <div
                class="slider-handle"
                :style="{ left: currentPercent + '%' }"
                @mousedown="startDrag"
                @touchstart="startDrag"
            ></div>
        </div>

        <div class="slider-ticks">
            <div
                v-for="(tick, index) in ticks"
                :key="index"
                class="tick"
                :style="{ left: tick.position + '%' }"
            >
                <div class="tick-mark"></div>
                <div class="tick-label">{{ tick.label }}</div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'plugin-home-possible-earnings-range-5',
    props: {
        value: {
            type: Number,
            default: 100000,
        },
    },
    data() {
        const values = [];
        const percentages = [];

        // 1. От 0% до 50%: от 100 000 до 1 000 000 с шагом 100 000 (10 значений)
        for (let i = 0; i <= 9; i++) {
            values.push(100000 + i * 100000);
            percentages.push((i / 9) * 50);
        }

        // 2. От 50% до 75%: от 1 000 000 до 10 000 000 с шагом 1 000 000 (9 новых значений)
        for (let i = 10; i <= 18; i++) {
            values.push(1000000 + (i - 9) * 1000000);
            percentages.push(50 + ((i - 9) / 9) * 25);
        }

        // 3. От 75% до 100%: от 10 000 000 до 100 000 000 с шагом 10 000 000 (9 новых значений)
        for (let i = 19; i <= 27; i++) {
            values.push(10000000 + (i - 18) * 10000000);
            percentages.push(75 + ((i - 18) / 9) * 25);
        }

        return {
            currentIndex: 0,
            values,
            percentages,
            isDragging: false,
        };
    },
    computed: {
        currentValue() {
            return this.values[this.currentIndex];
        },
        currentPercent() {
            return this.percentages[this.currentIndex];
        },
        formattedValue() {
            return this.formatValue(this.currentValue);
        },
        ticks() {
            return [
                { value: 100000, label: '100 тыс.', position: 0 },
                { value: 1000000, label: '500 тыс.', position: 22.5 },
                { value: 1000000, label: '1 млн.', position: 50 },
                { value: 10000000, label: '10 млн.', position: 75 },
                { value: 100000000, label: '100 млн.', position: 100 },
            ];
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(newVal) {
                let closestIndex = 0;
                let minDiff = Infinity;

                this.values.forEach((v, index) => {
                    const diff = Math.abs(v - newVal);
                    if (diff < minDiff) {
                        minDiff = diff;
                        closestIndex = index;
                    }
                });

                this.currentIndex = closestIndex;
            },
        },
    },
    methods: {
        startDrag(event) {
            this.isDragging = true;
            this.updateFromEvent(event);

            window.addEventListener('mousemove', this.onDrag);
            window.addEventListener('mouseup', this.stopDrag);
            window.addEventListener('touchmove', this.onDrag, { passive: false });
            window.addEventListener('touchend', this.stopDrag);
        },
        onDrag(event) {
            if (!this.isDragging) return;

            // Предотвращаем скролл страницы на мобильных устройствах при перетаскивании
            if (event.type === 'touchmove') {
                event.preventDefault();
            }

            this.updateFromEvent(event);
        },
        stopDrag() {
            this.isDragging = false;
            window.removeEventListener('mousemove', this.onDrag);
            window.removeEventListener('mouseup', this.stopDrag);
            window.removeEventListener('touchmove', this.onDrag);
            window.removeEventListener('touchend', this.stopDrag);
        },
        updateFromEvent(event) {
            const track = this.$refs.track;
            if (!track) return;

            const rect = track.getBoundingClientRect();
            // Получаем координату X (поддержка и мыши, и тача)
            const clientX = event.touches ? event.touches[0].clientX : event.clientX;

            // Вычисляем процент от 0 до 100
            let percent = ((clientX - rect.left) / rect.width) * 100;
            // Ограничиваем рамками 0-100%
            percent = Math.max(0, Math.min(100, percent));

            // Ищем ближайший разрешенный шаг
            let closestIndex = 0;
            let minDiff = Infinity;

            this.percentages.forEach((p, index) => {
                const diff = Math.abs(p - percent);
                if (diff < minDiff) {
                    minDiff = diff;
                    closestIndex = index;
                }
            });

            if (this.currentIndex !== closestIndex) {
                this.currentIndex = closestIndex;
                this.$emit('input', this.values[this.currentIndex]);
            }
        },
        formatValue(val) {
            if (val >= 1000000) {
                const millions = val / 1000000;
                return millions % 1 === 0
                    ? `${millions} млн. руб.`
                    : `${millions.toFixed(1)} млн. руб.`;
            } else if (val >= 1000) {
                const thousands = Math.round(val / 1000);
                return `${thousands} тыс. руб.`;
            }
            return `${val} руб.`;
        },
    },
    beforeDestroy() {
        this.stopDrag();
    },
};
</script>

<style lang="less" scoped>
@import '~theme';

@white: #ffffff;
@black: #000000;

@slider-height: 4px;
@handle-size: 12px;
@transition-speed: 0.1s;

.flex-center() {
    display: flex;
    align-items: center;
    justify-content: center;
}

.range-slider {
    position: relative;
    padding-top: 40px;
    user-select: none;
    max-width: 800px;
    margin: 0 auto;

    .slider-tooltip {
        position: absolute;
        top: -8px;
        left: 0;
        transform: translateX(-50%);
        background: @color-primary;
        color: @white;
        padding: 6px 12px;
        border-radius: 6px;
        font-size: 14px;
        font-weight: 500;
        white-space: nowrap;
        z-index: 10;

        &::after {
            content: '';
            position: absolute;
            bottom: -5px;
            left: 50%;
            transform: translateX(-50%);
            border-width: 6px 6px 0;
            border-style: solid;
            border-color: @color-primary transparent transparent transparent;
        }
    }

    .slider-track {
        position: relative;
        height: @slider-height;
        background: #E5DAF5;
        border-radius: 2px;
        cursor: pointer;
    }

    .slider-fill {
        position: absolute;
        height: 100%;
        background: @color-primary;
        border-radius: 2px;
        pointer-events: none;
    }

    .slider-handle {
        position: absolute;
        top: 50%;
        left: 0;
        transform: translate(-50%, -50%);
        width: @handle-size;
        height: @handle-size;
        background: @white;
        border: 1px solid @color-gray-main;
        border-radius: 50%;
        cursor: grab;
        // box-shadow: 0 2px 6px rgba(@black, 0.15);

        &:active {
            cursor: grabbing;
            transform: translate(-50%, -50%) scale(1.1);
        }
    }

    .slider-ticks {
        position: relative;
        height: 40px;
        margin-top: 15px;
    }

    .tick {
        position: absolute;
        left: 0;
        transform: translateX(-50%);
        text-align: center;
    }

    .tick-mark {
        width: 1px;
        height: 8px;
        background: @color-gray-main;
        margin: 0 auto 8px;
    }

    .tick-label {
        .typography-caption();

        font-size: 12px;
        color: @color-gray-main;
        white-space: nowrap;
    }
}
</style>
